import { formatBillingResetDate, formatBillingOneOffPrice } from "./hiringBillingDisplay";
import { formatPricingAmount } from "./pricingDisplay";

export const ADMIN_BONUS_JOB_SLOTS_NOTE = "Bonus jobs added by the JOBUWIN team";

const REASON_LABELS = {
  purchase: "Job slots purchased",
  publish: "Job slot used",
  admin_adjust: "Account adjustment",
  subscription_grant: "Subscription allowance",
  subscription_overage: "Extra job on subscription",
};

function transactionDeltaCount(transaction) {
  return Math.max(0, Math.floor(Math.abs(Number(transaction?.delta) || 0)));
}

export function isAdminBonusJobSlotsTransaction(transaction) {
  const reason = String(transaction?.reason || "").trim();
  const note = String(transaction?.note || "").trim();
  return reason === "admin_adjust" && note === ADMIN_BONUS_JOB_SLOTS_NOTE;
}

/**
 * @param {{ reason?: string, delta?: number, jobTitle?: string | null, note?: string | null }} transaction
 */
export function billingTransactionTitle(transaction) {
  const reason = String(transaction?.reason || "").trim();
  const count = transactionDeltaCount(transaction);

  if (isAdminBonusJobSlotsTransaction(transaction)) {
    return `${count} bonus job slot${count === 1 ? "" : "s"} from the JOBUWIN team`;
  }

  if (reason === "purchase") {
    return `${count} job slot${count === 1 ? "" : "s"} purchased`;
  }

  const base = REASON_LABELS[reason] || "Billing update";
  const jobTitle = String(transaction?.jobTitle || "").trim();
  if (reason === "publish" && jobTitle) {
    return `${base} — ${jobTitle}`;
  }
  if (reason === "publish" && count > 0) {
    return `${count} job slot${count === 1 ? "" : "s"} used`;
  }
  if (count > 0 && (reason === "subscription_grant" || reason === "subscription_overage")) {
    return `${count} job slot${count === 1 ? "" : "s"} — ${base.toLowerCase()}`;
  }
  return base;
}

/**
 * @param {{ reason?: string, note?: string | null }} transaction
 */
export function billingTransactionDescription(transaction) {
  const reason = String(transaction?.reason || "").trim();

  if (isAdminBonusJobSlotsTransaction(transaction)) {
    return "Added to your prepaid job slots.";
  }
  if (reason === "purchase") {
    return "Pay as you go purchase.";
  }
  if (reason === "publish") {
    return "Used when you published a role.";
  }
  if (reason === "subscription_grant") {
    return "Included with your subscription.";
  }
  if (reason === "subscription_overage") {
    return "Extra job on your subscription.";
  }

  const note = String(transaction?.note || "").trim();
  return note || "";
}

/**
 * Cost label for a billing history row. Uses only stored `transaction.amount` and
 * `transaction.currencyCode` — never current billing summary or advertising currency
 * (so country/currency changes do not rewrite past purchases).
 *
 * @param {{ reason?: string, amount?: number | null, currencyCode?: string | null, note?: string | null }} transaction
 */
export function billingTransactionCostLabel(transaction) {
  if (transaction?.amount != null) {
    const amount = Number(transaction.amount);
    const currencyCode = transaction?.currencyCode;
    if (Number.isFinite(amount) && currencyCode) {
      return formatPricingAmount(currencyCode, amount);
    }
  }

  const reason = String(transaction?.reason || "").trim();
  if (reason === "publish") {
    return "Included";
  }

  if (reason === "subscription_grant" || reason === "subscription_overage") {
    return "Included";
  }

  return "—";
}

/** @param {{ amount?: number | null }} transaction */
export function billingTransactionCostIsZero(transaction) {
  return transaction?.amount != null && Number(transaction.amount) === 0;
}

/**
 * @param {{ reason?: string, note?: string | null }} transaction
 * @returns {"bonus" | "purchase" | "publish" | "subscription" | "default"}
 */
export function billingTransactionVisualKind(transaction) {
  if (isAdminBonusJobSlotsTransaction(transaction)) return "bonus";
  const reason = String(transaction?.reason || "").trim();
  if (reason === "purchase") return "purchase";
  if (reason === "publish") return "publish";
  if (reason === "subscription_grant" || reason === "subscription_overage") return "subscription";
  return "default";
}

/**
 * @param {{ delta?: number }} transaction
 */
export function billingTransactionDeltaLabel(transaction) {
  const delta = Number(transaction?.delta) || 0;
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

/**
 * @param {{ billingType?: string, prepaidJobSlots?: number, planName?: string, jobsRemainingThisPeriod?: number, jobsIncludedPerMonth?: number, periodEndsAt?: string } | null | undefined} summary
 * @returns {{ typeLine: string, balanceLine: string }}
 */
export function purchasesAccountSummaryLines(summary) {
  if (!summary || summary.billingType === "pay_as_you_go") {
    const slots = Math.max(0, Math.floor(Number(summary?.prepaidJobSlots) || 0));
    return {
      typeLine: "Your account is currently a Pay as you go account.",
      balanceLine: `You have ${slots} job slot${slots === 1 ? "" : "s"} available.`,
    };
  }

  const remaining = Math.max(0, Math.floor(Number(summary.jobsRemainingThisPeriod) || 0));
  const included = Math.max(0, Math.floor(Number(summary.jobsIncludedPerMonth) || 0));
  const planName = String(summary.planName || "Subscription").trim() || "Subscription";
  const resetLabel = formatBillingResetDate(summary.periodEndsAt);
  const resetPart = resetLabel ? ` Resets ${resetLabel}.` : "";
  return {
    typeLine: `Your account is on the ${planName} subscription.`,
    balanceLine: `You have ${remaining} of ${included} jobs left this month.${resetPart}`,
  };
}

export { formatBillingOneOffPrice };
